/**
 * Pon Accessoire Catalogus enrichment on dealer accessories.
 *
 * Accessories from /api/cst/accessories can carry an optional `PonAccessoryCatalog`
 * object (photos, descriptions, category tree, stock, parts/labour/consumer prices
 * and their action counterparts). The enrichment is display-only: the Autoline
 * price (`Price`) stays leading on the quote, and the enrichment must never be
 * written to the quote payload (see `stripEnrichment`).
 */

/** The catalog bills labour in TE ("tijdseenheden"); 100 TE is one hour. */
const TE_PER_HOUR = 100

/**
 * Null-safe numeric coercion, tolerant of the NL decimal comma the way
 * `currencyHelper.fromString` is: an input the user typed as `1.185,92` — or the
 * `152,90` a component reads back out of a text field — is a comma-decimal, not
 * a thousands separator, so a string with a comma and no dot is rewritten first.
 * Returns null for null/undefined/'' and for anything that will not parse.
 */
export const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return null
  const text =
    typeof value === 'string' && value.includes(',') && !value.includes('.')
      ? value.replace(',', '.')
      : value
  const number = Number(text)
  return isNaN(number) ? null : number
}

const toStringOrNull = (value) => {
  if (value === null || value === undefined) return null
  const text = String(value).trim()
  return text === '' ? null : text
}

/**
 * Last path segment of a URL, used as a document label when the catalog left the
 * name empty. Query string and fragment are dropped first.
 */
const fileNameFromUrl = (url) => {
  const text = toStringOrNull(url)
  if (!text) return null
  const path = text.split(/[?#]/)[0]
  const last = path.substring(path.lastIndexOf('/') + 1)
  if (!last) return null
  try {
    return decodeURIComponent(last)
  } catch (error) {
    // Malformed percent-encoding; the raw segment is still a usable label
    return last
  }
}

/**
 * Catalog documents (installation manuals, leaflets) as `{ Name, Url }` pairs.
 * Entries without a URL are dropped — there is nothing to link to.
 */
const normalizeDocuments = (documents) => {
  if (!Array.isArray(documents)) return []
  return documents
    .map((doc) => ({
      Name: toStringOrNull(doc?.Name) || fileNameFromUrl(doc?.Url),
      Url: toStringOrNull(doc?.Url),
    }))
    .filter((doc) => doc.Url)
}

/**
 * The members of a package accessory: their catalog entry plus the price the
 * member carries inside the package (regular and action). Entries without an
 * accessory id are dropped — there is nothing to identify them by.
 */
const normalizePackageContent = (content) => {
  if (!Array.isArray(content)) return []
  return content
    .map((item) => ({
      AccessoryId: toStringOrNull(item?.AccessoryId),
      Count: toNumber(item?.Count),
      PriceExcl: toNumber(item?.PriceExcl),
      VatAmount: toNumber(item?.VatAmount),
      PriceIncl: toNumber(item?.PriceIncl),
      ActionPriceExcl: toNumber(item?.ActionPriceExcl),
      ActionVatAmount: toNumber(item?.ActionVatAmount),
      ActionPriceIncl: toNumber(item?.ActionPriceIncl),
      // The member's own catalog entry, in the same shape as its parent's
      Catalog: normalizePonCatalog({ PonAccessoryCatalog: item?.Catalog }),
    }))
    .filter((item) => item.AccessoryId)
}

/**
 * Maps the raw `PonAccessoryCatalog` payload of an accessory to a stable,
 * defensively-coerced shape. Returns null when the accessory is not enriched.
 */
export const normalizePonCatalog = (accessory) => {
  const raw = accessory?.PonAccessoryCatalog
  if (!raw || typeof raw !== 'object') return null

  return {
    AccessoryId: toStringOrNull(raw.AccessoryId),
    Title: toStringOrNull(raw.Title),
    Description: toStringOrNull(raw.Description),
    RemarksHtml: toStringOrNull(raw.RemarksHtml),
    Category1: toStringOrNull(raw.Category1),
    Category2: toStringOrNull(raw.Category2),
    Category3: toStringOrNull(raw.Category3),
    Stock: toNumber(raw.Stock),
    ThumbnailUrl: toStringOrNull(raw.ThumbnailUrl),
    ImageUrls: Array.isArray(raw.ImageUrls) ? raw.ImageUrls.filter(Boolean) : [],
    Documents: normalizeDocuments(raw.Documents),
    PackageContent: normalizePackageContent(raw.PackageContent),
    VatPercent: toNumber(raw.VatPercent),
    LabourRate: toNumber(raw.LabourRate),
    LabourTe: toNumber(raw.LabourTe),
    LabourExcl: toNumber(raw.LabourExcl),
    LabourIncl: toNumber(raw.LabourIncl),
    PartsExcl: toNumber(raw.PartsExcl),
    PartsIncl: toNumber(raw.PartsIncl),
    ConsumerExcl: toNumber(raw.ConsumerExcl),
    ConsumerIncl: toNumber(raw.ConsumerIncl),
    ActionPartsExcl: toNumber(raw.ActionPartsExcl),
    ActionPartsIncl: toNumber(raw.ActionPartsIncl),
    ActionConsumerExcl: toNumber(raw.ActionConsumerExcl),
    ActionConsumerIncl: toNumber(raw.ActionConsumerIncl),
    ActionValidFrom: toStringOrNull(raw.ActionValidFrom),
    ActionValidTo: toStringOrNull(raw.ActionValidTo),
  }
}

export const isEnriched = (accessory) => !!accessory?.PonAccessoryCatalog

/**
 * The photos to show for an accessory. The catalog does not always deliver the
 * full-size images; when it only has a thumbnail that stands in as the gallery,
 * since a small photo tells the dealer more than no photo at all.
 */
export const galleryImages = (accessory) => {
  const pon = accessory?.PonAccessoryCatalog
  if (!pon) return []
  if (pon.ImageUrls?.length) return pon.ImageUrls
  return pon.ThumbnailUrl ? [pon.ThumbnailUrl] : []
}

/** A date-time with no `Z` and no `±hh:mm` offset — the shape the catalog sends. */
const OFFSET_NAIVE_DATE_TIME = /^\d{4}-\d{2}-\d{2}T[\d:.]+$/

/**
 * Parses a catalog timestamp as UTC.
 *
 * The backend sends UTC without the designator: an action running to the last
 * moment of 31 March arrives as `2026-03-31T21:59:59` (23:59:59 CEST) and one
 * starting on 1 May as `2026-04-30T22:00:00`. ES parses an offset-naive
 * date-TIME as LOCAL time, so reading those literally expired every action two
 * hours early in summer and one hour early in winter. Appending `Z` restores
 * the instant the backend meant.
 *
 * Strings that already carry an offset (`Z`, `+02:00`, `-05:00`) are left alone,
 * and so are date-ONLY strings — ES already parses those as UTC. Returns null
 * for anything that will not parse.
 */
const parseCatalogDate = (value) => {
  if (!value) return null
  const text = String(value).trim()
  const date = new Date(OFFSET_NAIVE_DATE_TIME.test(text) ? `${text}Z` : text)
  return isNaN(date.getTime()) ? null : date
}

/**
 * True when the accessory has an action price whose validity window contains `now`.
 * Missing (and unparseable) From/To bounds are treated as open-ended.
 */
export const hasActiveAction = (accessory, now = new Date()) => {
  const pon = accessory?.PonAccessoryCatalog
  if (!pon) return false
  if (pon.ActionPartsIncl === null && pon.ActionConsumerIncl === null) return false

  const from = parseCatalogDate(pon.ActionValidFrom)
  const to = parseCatalogDate(pon.ActionValidTo)
  if (from && now < from) return false
  if (to && now > to) return false
  return true
}

/** `['Interieur', 'Zonnedaken', 'Zonneschermen']` — empty array when not enriched. */
export const categoryPath = (accessory) => {
  const pon = accessory?.PonAccessoryCatalog
  if (!pon) return []
  return [pon.Category1, pon.Category2, pon.Category3].filter(Boolean)
}

/** The members of a package accessory — empty for a regular accessory. */
export const packageContent = (accessory) => accessory?.PonAccessoryCatalog?.PackageContent || []

export const isPackage = (accessory) => packageContent(accessory).length > 0

/** Money compare/round on whole cents, so float noise never counts as a difference. */
const cents = (value) => Math.round(value * 100)
const round2 = (value) => cents(value) / 100

/**
 * The catalog prices a package line for all `Count` pieces of it. Every price in
 * the detail view is a price per piece — the catalog's own part price is — so a
 * line covering more than one piece is divided down to match.
 */
const perPiece = (value, count) => {
  if (value === null || value === undefined) return null
  return count > 1 ? round2(value / count) : value
}

/**
 * A package member presented as an accessory, so the detail view can render it
 * with the same computeds as a regular one. `Price` is what one piece of the
 * member costs inside the package — the action variant when the package itself
 * is sold at its action price — and is deliberately not an Autoline price.
 */
export const packageMemberAsAccessory = (item, atActionPrice = false) => {
  if (!item) return null
  const action = atActionPrice && item.ActionPriceIncl !== null
  const count = item.Count
  return {
    ID: item.AccessoryId,
    Name: item.Catalog?.Title || item.AccessoryId,
    Count: count,
    Price: {
      Excl: perPiece(action ? item.ActionPriceExcl : item.PriceExcl, count),
      Incl: perPiece(action ? item.ActionPriceIncl : item.PriceIncl, count),
      Btw: perPiece(action ? item.ActionVatAmount : item.VatAmount, count),
      BtwRate: item.Catalog?.VatPercent,
    },
    PonAccessoryCatalog: item.Catalog,
  }
}

export const hasLabour = (accessory) => (accessory?.PonAccessoryCatalog?.LabourTe ?? 0) > 0

/**
 * Labour time in hours. The catalog expresses it in TE ("tijdseenheden"), where
 * 100 TE is one hour — so `LabourTe: 50` is half an hour of montage.
 */
export const labourHours = (accessory) => {
  const te = accessory?.PonAccessoryCatalog?.LabourTe
  if (te === null || te === undefined) return null
  return te / TE_PER_HOUR
}

/**
 * The running action price as a `{ excl, incl }` pair — the installed variant for
 * accessories with labour. Null when no action is active or the catalog left the
 * relevant action fields empty.
 */
export const actionPrice = (accessory, now = new Date()) => {
  if (!hasActiveAction(accessory, now)) return null
  const pon = accessory.PonAccessoryCatalog
  const installed = hasLabour(accessory)
  const excl = installed ? pon.ActionConsumerExcl : pon.ActionPartsExcl
  const incl = installed ? pon.ActionConsumerIncl : pon.ActionPartsIncl
  if (excl === null || incl === null) return null
  return { excl, incl }
}

/**
 * True when an active action price is actually cheaper or dearer than the leading
 * Autoline price — i.e. when adding to the quote is a choice rather than a formality.
 * Compared on the excl. price, which is what the quote carries.
 */
export const actionPriceDiffers = (accessory, now = new Date()) => {
  const action = actionPrice(accessory, now)
  const autolineExcl = toNumber(accessory?.Price?.Excl)
  if (!action || autolineExcl === null) return false
  return cents(action.excl) !== cents(autolineExcl)
}

/**
 * Clone of the accessory priced at the action price, for quote rows the user
 * explicitly chose to put on the action price. Returns the accessory untouched
 * when there is no action price to apply.
 */
export const withActionPrice = (accessory, now = new Date()) => {
  const action = actionPrice(accessory, now)
  if (!action) return accessory
  return {
    ...accessory,
    Price: {
      ...accessory.Price,
      Excl: action.excl,
      Incl: action.incl,
      Btw: round2(action.incl - action.excl),
    },
  }
}

/**
 * Which side of the VAT line a single-price display shows. The catalog shows one
 * price per line, so the quote's price display mode decides which one that is.
 * Both values double as the suffix of the catalog's own price fields
 * (`Parts{Excl,Incl}`, `ActionConsumer{Excl,Incl}`, …) and as the key into `Price`.
 */
export const VAT_EXCL = 'Excl'
export const VAT_INCL = 'Incl'

/**
 * Which of the two prices the user picked for a row they put on the quote. The
 * catalog offers an action price alongside the leading Autoline price whenever
 * `actionPriceDiffers`, and the choice travels from the dialog to the catalog
 * container across an emit — as a constant rather than a bare string.
 */
export const PRICE_CHOICE_AUTOLINE = 'autoline'
export const PRICE_CHOICE_ACTION = 'action'

/** The `{ excl, incl }` side of an action price that `vat` asks for. */
const atVat = (pair, vat) => (vat === VAT_EXCL ? pair.excl : pair.incl)

/**
 * The price the catalog offers the accessory at: the running action price when
 * there is one, otherwise the leading Autoline price.
 */
export const offeredPrice = (accessory, vat = VAT_INCL, now = new Date()) => {
  const action = actionPrice(accessory, now)
  if (action) return atVat(action, vat)
  return toNumber(accessory?.Price?.[vat])
}

/** The regular (non-action) catalog price — installed when there is labour. */
export const regularPrice = (accessory, vat = VAT_INCL) => {
  const pon = accessory?.PonAccessoryCatalog
  if (!pon) return null
  return hasLabour(accessory) ? pon[`Consumer${vat}`] : pon[`Parts${vat}`]
}

/**
 * The struck "was" price for a row displayed at `current`: the price a running
 * action replaces — the higher of the Autoline price and the catalog's regular
 * price, whichever the action undercuts.
 *
 * Only an accessory on an action has one. Without an action there is nothing to
 * compare the price to: Autoline and the catalog carry the same money rounded
 * twice — Autoline sells at 1185.92 incl. where the catalog lists 1186.00 — and
 * striking one against the other presented a discount that did not exist.
 */
export const wasPrice = (accessory, current = null, vat = VAT_INCL, now = new Date()) => {
  if (!actionPrice(accessory, now)) return null
  const autoline = toNumber(accessory?.Price?.[vat])
  const displayed = current === null ? offeredPrice(accessory, vat, now) : toNumber(current)
  const alternatives = [autoline, regularPrice(accessory, vat)].filter((value) => value !== null)
  const regular = alternatives.length ? Math.max(...alternatives) : null
  if (regular === null || displayed === null) return null
  // Nothing to strike when the row already shows the regular price
  if (cents(regular) <= cents(displayed)) return null
  return regular
}

/** True when the quote row's price no longer matches the catalog (Autoline) price. */
export const isPriceEdited = (quoteAccessory, catalogAccessory) => {
  const quotePrice = toNumber(quoteAccessory?.Price?.Excl)
  const catalogPrice = toNumber(catalogAccessory?.Price?.Excl)
  if (quotePrice === null || catalogPrice === null) return false
  // Whole cents, like every other money compare here: 1185.92 and
  // 1185.9200000000001 are the same price, not an edit the user made
  return cents(quotePrice) !== cents(catalogPrice)
}

export const isNameEdited = (quoteAccessory, catalogAccessory) => {
  if (!quoteAccessory?.Name || !catalogAccessory?.Name) return false
  return quoteAccessory.Name !== catalogAccessory.Name
}

/**
 * Shallow clone without the `PonAccessoryCatalog` blob. Quote writes must go
 * through this so the enrichment never ends up in the preview-save payload.
 * Returns the original object untouched when it carries no enrichment.
 */
export const stripEnrichment = (accessory) => {
  if (!accessory || typeof accessory !== 'object') return accessory
  if (!('PonAccessoryCatalog' in accessory)) return accessory
  // eslint-disable-next-line no-unused-vars
  const { PonAccessoryCatalog, ...rest } = accessory
  return rest
}

export default {
  VAT_EXCL,
  VAT_INCL,
  PRICE_CHOICE_AUTOLINE,
  PRICE_CHOICE_ACTION,
  toNumber,
  normalizePonCatalog,
  isEnriched,
  galleryImages,
  hasActiveAction,
  categoryPath,
  packageContent,
  isPackage,
  packageMemberAsAccessory,
  hasLabour,
  labourHours,
  actionPrice,
  actionPriceDiffers,
  withActionPrice,
  offeredPrice,
  regularPrice,
  wasPrice,
  isPriceEdited,
  isNameEdited,
  stripEnrichment,
}
